import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { Package } from "lucide-react";
import { getOrder } from "../features/order/orderSlice";

const placeholder = new URL("../../assets/placeholder.png", import.meta.url)
  .href;

function mapOrderStatus(status) {
  switch ((status || "").toLowerCase()) {
    case "pending":
      return "Offen";
    case "processing":
      return "In Bearbeitung";
    case "paid":
      return "Bezahlt";
    case "shipped":
      return "Versendet";
    case "completed":
      return "Abgeschlossen";
    case "cancelled":
    case "canceled":
      return "Storniert";
    case "failed":
      return "Fehlgeschlagen";
    default:
      return status || "—";
  }
}

function mapPaymentStatus(ps) {
  switch ((ps || "").toLowerCase()) {
    case "created":
      return "Erstellt";
    case "initialized":
      return "Initialisiert";
    case "authorized":
      return "Autorisiert";
    case "captured":
    case "paid":
      return "Bezahlt";
    case "failed":
      return "Fehlgeschlagen";
    case "cancelled":
    case "canceled":
      return "Abgebrochen";
    default:
      return ps || "—";
  }
}

function badgeClass(kind, value) {
  const v = (value || "").toLowerCase();
  const base =
    "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold border";

  // Payment
  if (kind === "payment") {
    if (v === "paid" || v === "captured")
      return `${base} bg-green-50 text-green-700 border-green-200`;
    if (v === "failed") return `${base} bg-red-50 text-red-700 border-red-200`;
    if (v === "cancelled" || v === "canceled")
      return `${base} bg-gray-50 text-gray-700 border-gray-200`;
    return `${base} bg-yellow-50 text-yellow-800 border-yellow-200`; // created/initialized/authorized/unknown
  }

  // Order status
  if (v === "paid" || v === "completed")
    return `${base} bg-green-50 text-green-700 border-green-200`;
  if (v === "failed" || v === "cancelled" || v === "canceled")
    return `${base} bg-red-50 text-red-700 border-red-200`;
  if (v === "shipped")
    return `${base} bg-blue-50 text-blue-700 border-blue-200`;
  return;
}

function fmtMoney(val, currency = "CHF") {
  const n = Number(val ?? 0);
  return `${n.toFixed(2)} ${currency}`;
}

export default function Bestellungen() {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const { orders, loading, error } = useSelector((state) => state.order);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    if (user?.idUser) dispatch(getOrder());
  }, [user?.idUser, dispatch]);

  const groupedOrders = useMemo(() => {
    const acc = {};
    for (const row of orders || []) {
      const id = row.order_id;
      if (!acc[id]) {
        acc[id] = {
          order_id: id,
          order_no: row.order_no,
          created_at: row.created_at,
          status: row.status,
          payment_status: row.payment_status,
          currency: row.currency || "CHF",
          shipping_cost: row.shipping_cost ?? 0,
          total_amount: row.total_amount ?? null,
          shipping: {
            name: row.shipping_name,
            street: row.shipping_street,
            zip: row.shipping_zip,
            city: row.shipping_city,
            country: row.shipping_country,
          },
          items: [],
        };
      }
      acc[id].items.push(row);
    }
    return Object.values(acc).sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at),
    );
  }, [orders]);

  // Beim ersten Laden die neueste Bestellung geöffnet anzeigen. Nur einmal,
  // damit der Nutzer sie danach auch wieder einklappen kann.
  const didAutoOpen = useRef(false);
  useEffect(() => {
    if (!didAutoOpen.current && groupedOrders.length > 0) {
      didAutoOpen.current = true;
      setOpenId(groupedOrders[0].order_id);
    }
  }, [groupedOrders]);

  if (!user?.idUser) {
    return (
      <p className="kn-page text-center text-knapp-ink2 pt-16">
        Bitte einloggen, um Bestellungen zu sehen.
      </p>
    );
  }

  return (
    <div className="kn-page">
      {loading && (
        <div className="fixed top-0 left-0 right-0 z-50">
          <div className="mx-2 mt-1 h-[2px] bg-gray-300 opacity-80 rounded-full animate-pulse" />
        </div>
      )}

      {error && (
        <p className="text-center text-knapp-accent font-medium pt-4">
          Fehler:{" "}
          {typeof error === "string"
            ? error
            : "Bestellungen konnten nicht geladen werden."}
        </p>
      )}

      {!loading && (!orders || orders.length === 0) ? (
        <div className="flex flex-col items-center justify-center mt-24 px-6">
          <div className="kn-card p-8 max-w-md w-full text-center">
            <div className="flex justify-center mb-4">
              <Package className="h-12 w-12 text-knapp-accent" />
            </div>
            <h1 className="kn-h3 mb-2">Noch keine Bestellungen</h1>
            <p className="kn-body mb-6">
              Sobald Sie etwas bestellen, finden Sie Ihre Bestellungen hier.
            </p>
            <Link to="/produkte" className="kn-btn-primary">
              Zu den Produkten
            </Link>
          </div>
        </div>
      ) : (
        <main className="max-w-[1000px] mx-auto px-5 sm:px-8 lg:px-12 pt-11 pb-20">
          <div className="kn-eyebrow">Konto</div>
          <h1 className="kn-h1 mt-3 mb-7">Meine Bestellungen</h1>

          {groupedOrders.map((o) => {
            const isOpen = openId === o.order_id;
            return (
              <section key={o.order_id} className="kn-card p-[22px] mb-5">
                <div
                  className="flex flex-wrap items-center gap-3 justify-between cursor-pointer"
                  onClick={() => setOpenId(isOpen ? null : o.order_id)}
                  role="button"
                  tabIndex={0}
                  aria-expanded={isOpen}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setOpenId(isOpen ? null : o.order_id);
                    }
                  }}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="w-[26px] h-[26px] shrink-0 rounded-full border border-knapp-borderInput flex items-center justify-center text-[15px] text-knapp-ink3">
                      {isOpen ? "−" : "+"}
                    </span>
                    <span className="text-lg font-semibold">
                      Bestellung {o.order_no} –{" "}
                      {new Date(o.created_at).toLocaleDateString("de-DE")}
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-3 items-center text-sm text-knapp-ink3">
                    <span className="flex items-center gap-2">
                      <span>Status:</span>
                      <span className={badgeClass("order", o.status)}>
                        {mapOrderStatus(o.status)}
                      </span>
                    </span>
                    <span className="flex items-center gap-2">
                      <span>Zahlung:</span>
                      <span className={badgeClass("payment", o.payment_status)}>
                        {mapPaymentStatus(o.payment_status)}
                      </span>
                    </span>
                  </div>
                </div>

                {isOpen && (
                  <div className="mt-4 border-t border-knapp-border">
                    {o.items.map((item) => (
                      <div
                        key={item.order_items_id}
                        className="flex items-center gap-[18px] flex-wrap py-4 border-b border-knapp-borderSubtle"
                      >
                        <img
                          src={item.Bild || placeholder}
                          alt={item.Name}
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = placeholder;
                          }}
                          className="w-[62px] h-[62px] shrink-0 object-contain rounded-[10px] border border-knapp-borderSubtle bg-knapp-surface p-1"
                        />
                        <div className="flex-1 min-w-[180px]">
                          <div className="text-base font-semibold leading-tight">
                            {item.Name}
                          </div>
                          <div className="text-sm text-knapp-ink3 mt-1">
                            Menge: {item.quantity} · Stück:{" "}
                            {fmtMoney(item.unit_price, o.currency)}
                          </div>
                        </div>
                        <div className="text-[17px] font-bold text-knapp-accent whitespace-nowrap">
                          {fmtMoney(item.line_total, o.currency)}
                        </div>
                      </div>
                    ))}

                    <div className="flex justify-between gap-4 text-[15px] text-knapp-ink2 pt-3.5">
                      <span>Versand</span>
                      <strong className="font-semibold text-knapp-ink">
                        {fmtMoney(o.shipping_cost, o.currency)}
                      </strong>
                    </div>
                    <div className="flex justify-between gap-4 items-baseline pt-2">
                      <span className="text-base font-semibold">Gesamt</span>
                      <strong className="text-xl font-bold text-knapp-accent">
                        {o.total_amount != null
                          ? fmtMoney(o.total_amount, o.currency)
                          : "—"}
                      </strong>
                    </div>

                    {(o.shipping?.street || o.shipping?.city) && (
                      <div className="mt-5 pt-[18px] border-t border-knapp-border">
                        <div className="kn-legend mb-2">Lieferadresse</div>
                        <div className="text-[15px] leading-relaxed text-knapp-ink2">
                          <div>{o.shipping?.name}</div>
                          <div>{o.shipping?.street}</div>
                          <div>
                            {o.shipping?.zip} {o.shipping?.city}
                          </div>
                          <div>{o.shipping?.country}</div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </section>
            );
          })}
        </main>
      )}
    </div>
  );
}
